// Parses the backend member report (see sagas/users.ts exportMemberReport)
// and shapes it for the folder- and user-oriented report views.
//
// The CSV is semicolon separated, one line per principal, with the roles and
// the folder paths they apply to as parallel " | " separated lists:
//   UserId;PrincipalType;Email;Name;Roles;ProjectPaths
//   u1;UserIdentity;a@b.com;Ada;Owner | Reader;/ | Lake/

export type MemberReportEntry = {
  userId: string
  principalType: string
  email: string
  name: string
  role: string
  // Folder names from the site root down; [] is the site root itself.
  path: string[]
  // The platform no longer resolves this identity (deleted account etc.).
  unknown: boolean
}

export type FolderTreeRow = {
  id: string
  hierarchy: string[]
  type: 'folder' | 'member'
  name: string
  email?: string
  role?: string
  unknown?: boolean
  // Folders: explicit members of this folder.
  memberCount?: number
  // Folders: the report can't tell same-named sibling folders apart, so they
  // collapse into one node; this is how many were merged.
  occurrences?: number
}

export type UserTreeRow = {
  id: string
  hierarchy: string[]
  type: 'user' | 'folder'
  name: string
  email?: string
  role?: string
  unknown?: boolean
  // Users: number of folder assignments and their strongest role.
  folderCount?: number
  highestRole?: string
  // Folders: see FolderTreeRow.occurrences.
  occurrences?: number
}

const LIST_SEPARATOR = ' | '
const UNKNOWN = 'Unknown'
const ROLE_RANK = ['Reader', 'Contributor', 'Owner']

const splitList = (value: string | undefined) =>
  value ? value.split(LIST_SEPARATOR).map(item => item.trim()) : []

const toSegments = (path: string) => path.split('/').filter(Boolean)

export const parseMemberReport = (csv: string): MemberReportEntry[] => {
  const lines = csv
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter(line => line.trim().length > 0)
  if (lines.length === 0) return []

  // Look columns up by header name so a reordered report still parses.
  const header = lines[0].split(';').map(column => column.trim())
  const column = (name: string) => header.indexOf(name)
  const idx = {
    userId: column('UserId'),
    principalType: column('PrincipalType'),
    email: column('Email'),
    name: column('Name'),
    roles: column('Roles'),
    paths: column('ProjectPaths')
  }

  return lines.slice(1).flatMap(line => {
    const cells = line.split(';')
    const email = cells[idx.email]?.trim() ?? ''
    const name = cells[idx.name]?.trim() ?? ''
    const roles = splitList(cells[idx.roles])
    const paths = splitList(cells[idx.paths])
    const base = {
      userId: cells[idx.userId]?.trim() ?? '',
      principalType: cells[idx.principalType]?.trim() ?? '',
      email: email === UNKNOWN ? '' : email,
      name: name === UNKNOWN ? '' : name,
      unknown: email === UNKNOWN && name === UNKNOWN
    }

    const pairs = Math.min(roles.length, paths.length)
    return Array.from({ length: pairs }, (_, i) => ({
      ...base,
      role: roles[i],
      path: toSegments(paths[i])
    }))
  })
}

// The report lists every principal; the dialog shows users only.
export const onlyUsers = (entries: MemberReportEntry[]) =>
  entries.filter(entry => entry.principalType === 'UserIdentity')

export const highestRole = (roles: string[]) =>
  roles.reduce<string | undefined>(
    (best, role) =>
      best === undefined || ROLE_RANK.indexOf(role) > ROLE_RANK.indexOf(best)
        ? role
        : best,
    undefined
  )

// Folder -> members. Every folder on a path gets a row (even ones with no
// explicit members) so the tree mirrors the site's structure.
export const buildFolderTree = (
  entries: MemberReportEntry[],
  rootLabel: string
): FolderTreeRow[] => {
  const folders = new Map<string, FolderTreeRow>()
  const members = new Map<string, FolderTreeRow>()
  // Per user and folder, how many times the report listed it.
  const listings = new Map<string, number>()

  const ensureFolder = (hierarchy: string[]) => {
    const id = `folder:${hierarchy.join('/')}`
    if (!folders.has(id)) {
      folders.set(id, {
        id,
        hierarchy,
        type: 'folder',
        name: hierarchy[hierarchy.length - 1],
        memberCount: 0,
        occurrences: 1
      })
    }
    return folders.get(id)!
  }

  entries.forEach(entry => {
    const hierarchy = [rootLabel, ...entry.path]
    hierarchy.forEach((_, depth) => ensureFolder(hierarchy.slice(0, depth + 1)))
    const folder = ensureFolder(hierarchy)

    const listingKey = `${folder.id}|${entry.userId}`
    const listed = (listings.get(listingKey) ?? 0) + 1
    listings.set(listingKey, listed)
    folder.occurrences = Math.max(folder.occurrences ?? 1, listed)

    const memberId = `member:${folder.id}|${entry.userId}`
    const existing = members.get(memberId)
    if (existing) {
      existing.role = highestRole([existing.role!, entry.role])
      return
    }
    folder.memberCount = (folder.memberCount ?? 0) + 1
    members.set(memberId, {
      id: memberId,
      // The user id keeps same-named users apart; the grid shows `name`.
      hierarchy: [...hierarchy, entry.userId],
      type: 'member',
      name: entry.name,
      email: entry.email,
      role: entry.role,
      unknown: entry.unknown
    })
  })

  return [...folders.values(), ...members.values()]
}

// User -> the folders they're explicitly assigned to.
export const buildUserTree = (
  entries: MemberReportEntry[],
  rootLabel: string
): UserTreeRow[] => {
  const users = new Map<string, UserTreeRow>()
  const folders = new Map<string, UserTreeRow>()

  entries.forEach(entry => {
    let user = users.get(entry.userId)
    if (!user) {
      user = {
        id: `user:${entry.userId}`,
        hierarchy: [entry.userId],
        type: 'user',
        name: entry.name,
        email: entry.email,
        unknown: entry.unknown,
        folderCount: 0
      }
      users.set(entry.userId, user)
    }
    user.highestRole = highestRole(
      [user.highestRole, entry.role].filter(Boolean) as string[]
    )

    const pathLabel = [rootLabel, ...entry.path].join(' / ')
    const folderId = `folder:${entry.userId}|${pathLabel}`
    const existing = folders.get(folderId)
    if (existing) {
      existing.occurrences = (existing.occurrences ?? 1) + 1
      existing.role = highestRole([existing.role!, entry.role])
      return
    }
    user.folderCount = (user.folderCount ?? 0) + 1
    folders.set(folderId, {
      id: folderId,
      hierarchy: [entry.userId, pathLabel],
      type: 'folder',
      name: pathLabel,
      role: entry.role,
      occurrences: 1
    })
  })

  return [...users.values(), ...folders.values()]
}

export type Nested<T> = T & { subRows: Nested<T>[] }

// Turns flat rows keyed by `hierarchy` into nested rows for the table's
// `getSubRows`. Rows whose parent isn't in the list become roots.
export const nestByHierarchy = <T extends { hierarchy: string[] }>(
  rows: T[]
): Nested<T>[] => {
  const nodes = new Map<string, Nested<T>>(
    rows.map(row => [row.hierarchy.join('\u0000'), { ...row, subRows: [] }])
  )
  const roots: Nested<T>[] = []
  nodes.forEach(node => {
    const parent = nodes.get(node.hierarchy.slice(0, -1).join('\u0000'))
    if (parent && node.hierarchy.length > 1) parent.subRows.push(node)
    else roots.push(node)
  })
  return roots
}

export type CachedMemberReport = { csv: string; generatedAt: string }

// The report lists every user's email and role, so it's cached per tab
// (sessionStorage), not persisted across sessions.
// v2: reports now list every principal. Ones cached under the old key hold
// users only, so they're never read, but still cleared: tabs open across the
// upgrade may hold them.
const CACHE_PREFIX = 'member-report-v2:'
const LEGACY_CACHE_PREFIX = 'member-report:'
const cacheKey = (tenantId: string) => `${CACHE_PREFIX}${tenantId}`

export const readCachedMemberReport = (
  tenantId: string
): CachedMemberReport | null => {
  try {
    const cached = JSON.parse(
      sessionStorage.getItem(cacheKey(tenantId)) ?? 'null'
    )
    return typeof cached?.csv === 'string' &&
      typeof cached?.generatedAt === 'string'
      ? cached
      : null
  } catch {
    return null
  }
}

export const cacheMemberReport = (
  tenantId: string,
  report: CachedMemberReport
) => {
  try {
    sessionStorage.setItem(cacheKey(tenantId), JSON.stringify(report))
  } catch {
    // Storage full or unavailable: the report just isn't cached.
  }
}

// Drops cached reports on logout, or those of other tenants after a switch.
export const clearMemberReportCache = (keepTenantId?: string) => {
  try {
    Object.keys(sessionStorage)
      .filter(
        key =>
          key.startsWith(LEGACY_CACHE_PREFIX) ||
          (key.startsWith(CACHE_PREFIX) &&
            (!keepTenantId || key !== cacheKey(keepTenantId)))
      )
      .forEach(key => sessionStorage.removeItem(key))
  } catch {
    // Storage unavailable: nothing was cached either.
  }
}
